/**
 * @Copyright 2014-2018 DataVoke, Inc. All rights reserved.
 */

'use strict';

import pushHandlers from './pushHandlers';
import EventEmitter from '../support/eventemitter';
import * as tools from '../utils/tools';
import exponentialBackoff from '../utils/exponentialBackoff';
import * as types from '../utils/dvtypes';
import Endpoints from './endpoints';

export class ServerSession extends EventEmitter {
    constructor () {
        super();
        this._sessionGUID = null;
        this.User = null;
        this.MemberOf = [];
    }

    get sessionGUID () {
        return this._sessionGUID;
    }

    set sessionGUID (value) {
        if (value !== this._sessionGUID) {
            this._sessionGUID = value;
            if (value != null && value !== '') {
                this.emit('authenticated', this);
            }
            else {
                this.emit('loggedout', this);
            }
        }
    }
}

async function waitOne () {
    return new Promise((resolve) => {
        setTimeout(resolve, 1000);
    });
}

export class SocketSession {
    constructor () {
        //#region Internals...
        let $this = this;

        this._requests = new tools.Dictionary();
        this._handlers = new tools.Dictionary();
        this._lastReqID = 0;

        /**
         * GUID that identifies the active client session (persists when a socket reconnects)
         * @type {string}
         * @private
         */
        this._clientSessionGUID = tools.newUID();

        this._pendingDataRequests = [];

        this._rawSocket = null;

        this._systemPushHandlers = pushHandlers;

        /**
         * Flag to indicate if the connection is being reset.
         * @type {boolean}
         * @private
         */
        this._resetReconnectFlag = false;

        /**
         * This value will be for inquiry on what the socket is doing.
         * @type {number}
         */
        this._socketState = types.SocketStates.CLOSED;

        /**
         * This variable is used for a back off iterator in case the socket is reconnecting.
         * @type {Iterator|null}
         * @private
         */
        this._backoffIterator = null;

        /**
         * The object that contains information about the session with the server.
         * @type {ServerSession}
         */
        this.serverSession = new ServerSession();

        this.serverSession.on('authenticated', (session) => {
            $this._socketState = types.SocketStates.AUTHENTICATED;

            // send any requests pending authentication...
            if ($this._pendingDataRequests.length > 0) {
                $this._pendingDataRequests.forEach((data) => {
                    data.tkn = $this.serverSession.sessionGUID;
                    $this._sendRawMessage(data);
                });

                // clear out the requests so we don't resend again.
                $this._pendingDataRequests.length = 0;
            }
        });

        this.serverSession.on('loggedout', (session) => {
            switch ($this._socketState) {
                case types.SocketStates.AUTHENTICATED:
                    // if we are "authenticated", then we are logged out, otherwise we don't change state.
                    $this._socketState = types.SocketStates.OPEN;
                    break;
                default:
                    break;
            }
        });

        // Run a keep alive task every 30 minutes to keep the load balancer from closing the connection...EK
        this.keepAliveTaskHandle = setInterval(() => {
            $this.call('Support', 'KeepAlive', {}, () => console.info('Keep alive...'));
        }, 30 * 60 * 1000);
    }

    /**
     * Sends data to the service (raw text)... EK
     * @param data - raw data to send to the service (should be a JSON representation of a request).
     */
    _sendRawMessage (data) {
        switch (this.state()) {
            case types.SocketStates.CLOSED:
            case types.SocketStates.CONNECTING:
                // we are not in a state where we can even authenticate. :(
                this._pendingDataRequests.push(data);
                this._resetReconnectTimer();
                return;
            case types.SocketStates.OPEN:
                // at this point we have a connection and can try to authenticate, etc.
                let endpoint = Endpoints.find(e => e.svc === data.svc && e.fn === data.fn);
                if (!endpoint || !endpoint.authRequired) {
                    // this endpoint does not require authentication, we can allow it.
                    break;
                }

                // otherwise, the endpoint does require authentication - pend the request until we are authenticated.
                this._pendingDataRequests.push(data);
                this._resetReconnectTimer();
                return;
            case types.SocketStates.AUTHENTICATED:
            default:
                // any and all communication should be able to get through.
                break;
        }

        if (typeof data !== 'string') {
            data = JSON.stringify(data);
        }

        this._rawSocket.send(data);
    }

    /**
     * Reads all data from the service (raw data).  Calls event handlers setup for callbacks or push events...EK
     * @param event - The raw data event from the underlying WebSocket connection.
     */
    _readRawMessage (event) {
        let res = JSON.parse(event.data);

        //Get the callback handler based on the id sent by the client.. If the ID is negative (this means this is a server push notification) ...EK
        if (res.pushEvent == null) {
            //console.log('processing response: ' + res.id.toString());
            let req = this._requests.getVal(res.id);
            if (req != null && req.handle != null) {
                req.handle(res.res, res.err);

                if (res.fin === true)
                    this._requests.remove(res.id);            //Drop the reference to allow for memory clean-up... EK
            }
            else {
                if (res.err != null) {
                    console.log('Error from service (without client handler): ', res.err);
                }

                if (res.id != null) {
                    console.log('Missing handle to request id (check services requests): ', res.id);
                }

                if (res.serviceVersion != null) {
                    if (app.dv.dvPackage.version !== res.serviceVersion && app.dv.cache.session.isDesigner && !window.cordova) {
                        // The version of the service is now different than the version in the client's package.json...
                        // Allow the session to determine how to handle this if the user has logged in
                        if (app.dv.cache.session) {
                            app.dv.cache.session.handleVersionMismatch(res.serviceVersion)
                        }
                        else {
                            app.dv.mismatchedVersion(res.serviceVersion);
                        }
                    }
                }
            }
        }
        else {
            //based on the push event, call the handler in the handlers dictionary...EK
            let pushHandler = this._handlers.getVal(res.pushEvent) || this._systemPushHandlers[res.pushEvent];
            if (pushHandler != null){
                pushHandler(res.res);
            }
            else {
                console.log('No handler for service push notification - event: ', res.pushEvent);
            }
        }
    }

    /**
     * Attempts to reconnect to the DV service
     * @returns {Promise.<void>}
     */
    async _reconnect () {
        let $this = this;

        /**
         * Wait for the specified number of seconds without locking the thread.
         * @param {number} seconds
         * @returns {Promise<void>}
         */
        async function sleep(seconds) {
            let waitTime = 0;

            if (Number.isNumeric(seconds)) {
                waitTime = Number(seconds);
            }
            else {
                waitTime = 1;
            }

            for (let i = 0; i < waitTime; i++) {
                await waitOne();

                if ($this._resetReconnectFlag) {
                    break;
                }
            }
        }

        $this._backoffIterator = exponentialBackoff();
        let reconnected = false;

        // set public variable so inquiries can be made on our state.
        $this._socketState = types.SocketStates.CONNECTING;

        while(!reconnected) {
            let waitTime = $this._backoffIterator.next().value.toFixed(0);
            console.info(`Waiting ${waitTime}s before retrying socket connection`);

            await sleep(waitTime);

            // resetting this flag here prevents us from short-circuiting the wait time accidentally.
            $this._resetReconnectFlag = false;

            try {
                await $this.connect($this._uri, $this._protocol);

                console.info('Socket Reconnected');
                reconnected = true;
                $this._socketState = types.SocketStates.OPEN;

                if(app.dv.cache.session.currentUser.User) {
                    // Re-establish session..AK
                    return app.dv.services.call(app.dv.services.endpoints.authenticateUser, {
                        UserLoginID: app.dv.cache.session.currentUser.User.LoginID,
                        Password: app.dv.cache.session.currentUser.User.Definition.PasswordSignature,
                        isHash: true,
                        skipEntityDownload: true,
                    }).then((res) => {
                        res.User.Definition = tools.entityParse(res.User.Definition);
                        $this.serverSession.User = app.dv.cache.session.User = res.User;
                        $this.serverSession.MemberOf = app.dv.cache.session.MemberOf = res.MemberOf;
                        $this.serverSession.isDesigner = app.dv.cache.session._isDesigner = !!res.IsDesigner;
                        $this.serverSession.sessionGUID = app.dv.cache.session.sessionGUID = res.SessionGUID;
                    }).catch((err) => {
                        return console.error(err);
                    });
                } // Else user is not logged in, will not attempt reconnect
            } catch(e) {
                if (e) {
                    console.error(e.message, e);
                }

                // Ensure the the message is shown after each failed socket connection attempt
                if (!app.dv.isNoServiceConnection()) {
                    // Allow the session to determine how to handle the event if the user has logged in
                    if (app.dv.cache.session != null) {
                        if (!app.dv.cache.session.hasLostConnection) {
                            app.dv.cache.session.handleConnectionLost();
                        }
                    }
                    else {
                        app.dv.isNoServiceConnection(true);
                    }
                }

                console.info('Reconnect failed');
            }
        }
    }

    /**
     * Returns a Promise. Creates an underlying WebSocket and connects to a service URI.  Will establish connection recovery functions if the connection is lost...EK
     * @param {String} uri - The WebSocket URI location (i.e. wss://domain.com/clientSocket).
     * @param {String} protocol - The negotiated protocol, in our case it's echo-protocol.
     * @returns {Promise}
     */
    async connect (uri, protocol) {
        let $this = this;
        this._uri = uri;
        this._protocol = protocol;

        if ($this._rawSocket) {
            if ($this._rawSocket.readyState !== WebSocket.CLOSED && $this._rawSocket.readyState !== WebSocket.CLOSING) {
                // The old socket is still open... Make sure we close it before creating a new one
                this.close();
            }
        }

        return new Promise((resolve, reject) => {
            let opened = false;
            $this._socketState = types.SocketStates.CONNECTING;

            $this._rawSocket = new WebSocket(uri, protocol);

            $this._rawSocket.onerror = function (event) {
                console.error(event);

                if(!opened) reject();
            };

            $this._rawSocket.onclose = (event) => {
                console.error('Socket closed' + (event.reason ? ' (' + event.reason + ')' : ''), event);
                if ($this._socketState !== types.SocketStates.CONNECTING) {
                    $this._socketState = types.SocketStates.CLOSED;
                }

                if (opened) {
                    $this._reconnect();
                }
            };

            $this._rawSocket.onmessage = (event) => {
                $this._readRawMessage(event);
            };

            $this._rawSocket.onopen = (event) => {
                console.info('Socket opened');
                $this._socketState = types.SocketStates.OPEN;
                opened = true;

                // Allow the session to determine how to handle the event if the user has logged in
                if (app.dv.cache.session != null) {
                    app.dv.cache.session.handleConnectionRestored();
                }
                else {
                    app.dv.isNoServiceConnection(false);
                }

                // Re-send any requests that may exist from the previous connection
                this._requests.forEach((request) => {
                    if (request && request.req) {
                        this._sendRawMessage(request.req);
                    }
                });

                resolve(event);
            };
        });
    }

    /**
     * Method to reset the reconnection attempt
     * @private
     */
    _resetReconnectTimer () {
        this._backoffIterator = exponentialBackoff();
        this._resetReconnectFlag = true;
    }

    state () {
        return this._socketState;
    }

    /**
     * Calls a specific endpoint function on the connected service.
     * @param {string} service - The service name to call (i.e. Support).
     * @param {string} endpoint - The service function/endpoint to invoke.  (i.e Ping)
     * @param {Object} args - An object that represents data needed (if any) that is supplied to the service function.
     * @param {function} callback - A function, when specified, that is called when a response that corresponds with the request sent.
     *                  This callback could be called multiple times if there is more than one response sent without the "fin" or finished flag set.
     */
    call (service, endpoint, args, callback) {
        //Build a request object to tell the service what endpoint/logic to execute with the given args...EK
        let req = {
            /**
             * If we have established a session token, ensure it's give..EK
             * @type {string}
             */
            tkn: this.serverSession.sessionGUID,

            /**
             * Track a unique local ID used to ensure the proper callback is fired (Since all processing is ASYNC) ... EK
             * @type {number}
             */
            id: this._lastReqID++,

            /**
             * Track the client session guid. (used to identify client session during reconnects)
             * @type {string}
             */
            clientGUID: this._clientSessionGUID,

            /**
             * Send the specified service string
             * @type {string}
             */
            svc: service,

            /**
             * Send the specified endpoint string
             * @type {string}
             */
            fn: endpoint,

            /**
             * Send the specified arguments object
             * @type {Object}
             */
            args: args
        };

        if (callback != null) {
            //Push the local id and the callback that should handle the response from the server...EK
            this._requests.add(req.id, {
                handle: callback,
                req: req
            });
        }

        this._sendRawMessage(req);
    }


    /**
     * Closes the underlying socket (Note: The auto-recovery logic will recover the connection, unless prevented)
     * @param {Number} [code = 1000] - The required code that indicates why the socket was closed. Defaults to 1000 (Normal Closure)
     */
    close (code = 1000) {
        if (this._rawSocket != null) {
            // Clear the handlers off of the current socket first
            this._rawSocket.onerror = this._rawSocket.onclose = this._rawSocket.onmessage = this._rawSocket.onopen = null;

            this._rawSocket.close(code);
        }
    }

    /**
     * Used to add a user-defined service push event handler (These will override any system specified handlers)
     * @param {String} eventName - The push event name.
     * @param {Function} handle - The function that will be called if a push event of the given type is received.
     */
    addPushHandler (eventName, handle) {
        this._handlers.add(eventName, handle);
    }
}
